import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import { Query, QueryTasksArgs, Task } from '@lgg/isomorphic/types/__generated__/graphql';
import {
  CONTACT_LABEL_PREVIEW_FIELDS,
  mapTaskOrAppointmentEdgesToListItemData,
} from 'src/components/pages/dashboard/components/cards/shared/helpers';
import { TaskOrAppointmentList } from 'src/components/pages/dashboard/components/cards/shared/task-or-appointment-list';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  ChipFilters,
  EventFilterValue,
} from 'src/components/pages/dashboard/components/shared';
import { useTaskWhereInputResolver } from 'src/components/pages/tasks/hooks';
import { TasksPageQueryParams } from 'src/components/pages/tasks/shared';
import { CORE_PAGE_INFO_FIELDS } from 'src/components/providers/apollo-provider-provider';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useInfiniteListQuery } from 'src/hooks/use-infinite-list-query';
import {
  getLegacyFiltersQueryString,
  useFormatLegacyDateQueryString,
} from 'src/hooks/use-legacy-params-for-dashboard-query';

export const GET_DASHBOARD_TASKS_QUERY = gql`
  ${CORE_PAGE_INFO_FIELDS}
  ${CONTACT_LABEL_PREVIEW_FIELDS}
  # noinspection GraphQLUnresolvedReference,GraphQLSchemaValidation
  query GetTasks(
    $institutionId: Int!
    $first: Int
    $after: String
    $before: String
    $last: Int
    $where: TaskWhereInput
  ) {
    tasks(
      institutionId: $institutionId
      first: $first
      after: $after
      before: $before
      last: $last
      where: $where
      orderBy: { dueAt: ASC }
    ) @connection(key: "infinite-scroll") {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        cursor
        node {
          id
          title
          dueAt
          status
          assignedTo {
            id
            fullName
          }
          contact {
            ...ContactLabelPreviewFields
          }
          completedAt
        }
      }
    }
  }
`;

const BASE_TEST_ID = 'next-tasks-card';

const PAGE_SIZE = 10;

export const NextTasksCard = () => {
  const { t } = useTranslation(['common', 'dashboard']);
  const handleGraphQLError = useHandleGraphQLError();
  const { id: institutionId } = useCurrentInstitution();
  const { targetUserId } = useContext(DashboardFiltersContext);
  const whereInputResolver = useTaskWhereInputResolver();
  const [activeFilter, setActiveFilter] = useState<EventFilterValue>(
    EventFilterValue.TODAY,
  );
  const { endOfDay, initDayJsDate, startOfToday } = useDateHelpers();
  const { formatLegacyDateQueryString } = useFormatLegacyDateQueryString();

  const getDateFilterValues = useCallback(
    (type: 'QUERY' | 'QUERY-STRING') => {
      const isTypeQuery = type === 'QUERY';

      return activeFilter === EventFilterValue.TODAY
        ? {
            status: ['YET-TO-START', 'IN-PROGRESS', 'COMPLETED'],
            due_at_from: isTypeQuery
              ? startOfToday().toISOString()
              : formatLegacyDateQueryString(startOfToday()),
            due_at_to: isTypeQuery
              ? endOfDay(new Date()).toISOString()
              : formatLegacyDateQueryString(endOfDay(new Date())),
          }
        : {
            status: ['YET-TO-START', 'IN-PROGRESS'],
            due_at_from: isTypeQuery
              ? initDayJsDate(new Date()).toISOString()
              : formatLegacyDateQueryString(initDayJsDate(new Date()).toDate()),
          };
    },
    [activeFilter, endOfDay, formatLegacyDateQueryString, initDayJsDate, startOfToday],
  );

  const baseTaskListQueryParams: TasksPageQueryParams = useMemo(
    () => ({
      'view-code': 'custom',
      institution_id: institutionId.toString(),
      q: {
        advanced: {
          agent: targetUserId?.toString(),
        },
      },
      table: 'sort|dueAt|direction|ASC',
    }),
    [institutionId, targetUserId],
  );

  const taskListQueryParams: TasksPageQueryParams = useMemo(
    () => ({
      ...baseTaskListQueryParams,
      q: {
        advanced: {
          ...baseTaskListQueryParams.q?.advanced,
          ...getDateFilterValues('QUERY'),
        },
      },
    }),
    [baseTaskListQueryParams, getDateFilterValues],
  );

  const taskListQueryStringParams: TasksPageQueryParams = useMemo(
    () => ({
      ...baseTaskListQueryParams,
      q: {
        advanced: {
          ...baseTaskListQueryParams.q?.advanced,
          ...getDateFilterValues('QUERY-STRING'),
        },
      },
    }),
    [baseTaskListQueryParams, getDateFilterValues],
  );

  const { handleLoadBottom, firstItemIndex, loadingMoreBottom, loading, nodes, error } =
    useInfiniteListQuery<Pick<Query, 'tasks'>, Partial<QueryTasksArgs>, Task>({
      queryOptions: {
        getNodeIdCallback: (notification) => notification?.id,
        query: GET_DASHBOARD_TASKS_QUERY,
        variables: {
          first: PAGE_SIZE,
          institutionId,
          where: whereInputResolver(taskListQueryParams),
        },
        getEdgesCallback: (data) => data?.tasks.edges ?? [],
        getPageInfoCallback: (data) => data?.tasks.pageInfo,
        onError: handleGraphQLError,
        queryPageSize: PAGE_SIZE,
      },
    });

  const isEmpty = nodes.length === 0 || !!error;

  const items = useMemo(
    () =>
      mapTaskOrAppointmentEdgesToListItemData(
        nodes,
        'dueAt',
        'assignedTo',
        undefined,
        'task',
      ),
    [nodes],
  );

  const cardFilters = useMemo(
    () => (
      <ChipFilters<EventFilterValue>
        options={[
          {
            label: t('common:pointInTime.today'),
            testId: `${BASE_TEST_ID}-filter-today`,
            value: EventFilterValue.TODAY,
          },
          {
            label: t('common:upcoming'),
            testId: `${BASE_TEST_ID}-filter-upcoming`,
            value: EventFilterValue.UPCOMING,
          },
        ]}
        selectedOption={activeFilter}
        onChanged={setActiveFilter}
      />
    ),
    [activeFilter, t],
  );

  const emptyStateKey =
    activeFilter === EventFilterValue.TODAY ? 'noTasksForToday' : 'noUpcomingTasks';

  return (
    <TaskOrAppointmentList
      title={t('dashboard:cards.nextTasks.title')}
      titleExtraContent={cardFilters}
      items={items}
      loading={loading}
      isEmpty={isEmpty}
      emptyStateIcon="task"
      emptyStateMessage={t(`dashboard:cards.nextTasks.${emptyStateKey}`)}
      dateLabel={t('dashboard:cards.nextTasks.due')}
      linkTo={`/i/company/${institutionId}/cms/tasks/?${getLegacyFiltersQueryString(
        taskListQueryStringParams,
      )}`}
      linkText={t('common:viewMore')}
      testId={BASE_TEST_ID}
      cardInfoI18nKey="dashboard:cards.nextTasks.tooltipDescription"
      firstItemIndex={firstItemIndex}
      handleLoadBottom={handleLoadBottom}
      loadingMoreBottom={loadingMoreBottom}
    />
  );
};
